import { Fragment, useRef, useState } from 'react';

import type { IconList, InputSize } from '@snowit/icetypes';
import type { SnowmentFormat } from '@snowit/utils/snowmentv2';
import type { ICalendar } from './Calendar/Calendar';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import { Unicodes } from '@snowit/iceconst';
import snowmentV2 from '@snowit/utils/snowmentv2';

import ButtonIcon from '../Buttons/ButtonIcon';
import Input from '../Inputs/Input';
import Typo from '../Typography/Typo';
import Calendar from './Calendar';
import CalendarWeek from './Calendar/CalendarWeek';

import cx from 'classnames';
import s from './DatePicker.module.scss';

interface IDatePicker {
  size?: InputSize;
  label?: string;
  placeholder?: string | string[];
  hint?: string;
  icon?: IconList;
  emoji?: boolean;
  dates: string[];
  dateFormat?: SnowmentFormat;
  name?: string;
  onChange: (dates: string[]) => void;
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMsg?: string;
  id?: string;
  classNames?: {
    wrapper?: string;
    wrapInput?: string;
    input?: string;
    dropdown?: string;
  };
  calendar?: Omit<ICalendar, 'onChange' | 'onChangeView' | 'value'>;
  modes?: {
    picker: 'range' | 'multi' | 'single';
    calendar: 'week' | 'month';
  };
  /** Jest/Cypress test string */
  test?: string;
  resettable?: boolean;
}

const DatePicker = (props: IDatePicker) => {
  const {
    size,
    icon = 'calendar_week',
    label,
    hint,
    error,
    errorMsg,
    loading,
    required,
    disabled,
    test,
    dates,
    calendar,
    dateFormat = 'short_date',
    onChange,
    modes = {
      picker: 'range',
      calendar: 'month',
    },
    classNames,
    resettable,
  } = props;
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [temp, setTemp] = useState<string>('');
  const [onlyDates, setOnlyDates] = useState<string[] | undefined>(calendar?.onlyDates);
  const parent = useRef<any>(null);
  const { minWidth } = useBreakpoints();

  const { maxDays = 6 } = calendar ?? {};
  const placeholder = Array.isArray(props.placeholder)
    ? props.placeholder.join(Unicodes.ARROW_RIGHT)
    : props.placeholder;

  const handleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const first = dates[0];
  const last = dates[dates.length - 1];

  const renderLabel = () => {
    const firstDate = snowmentV2(first).format(dateFormat);
    const lastDate = snowmentV2(last).format(dateFormat);
    if (first && first === last) return firstDate;
    if (calendar?.mode === 'range') {
      return [first && firstDate, last && lastDate].filter(Boolean).join(Unicodes.ARROW_RIGHT);
    }
    return first ? firstDate : '';
  };

  const handleChange = (date: string) => {
    if (calendar?.mode === 'single') {
      onChange([date]);
      setShowDropdown(false);
    } else {
      if (!temp) {
        setTemp(date);
        onChange([date]);
        if (maxDays) {
          const _min = snowmentV2(date).add(-maxDays, 'd').format();
          const _max = snowmentV2(date).add(maxDays, 'd').format();
          const enu = snowmentV2.enumerateDays(_min, _max);
          setOnlyDates(enu);
        }
      } else {
        const enumerated = snowmentV2.enumerateDays(temp, date);
        onChange(enumerated);
        setOnlyDates(undefined);
        setShowDropdown(false);
        setTemp('');
      }
    }
  };

  const handleReset = (args) => {
    args?.event?.stopPropagation();
    onChange([]);
    setTemp('');
    setShowDropdown(false);
    setOnlyDates(undefined);
  };

  const renderModalHeader = () => {
    if (minWidth.sm) return null;
    return (
      <header className={s.headerDropdown}>
        <Typo
          text={label || placeholder || ''}
          tag={'h2'}
          size={'df'}
          weight={'medium'}
          color={'text'}
        />
        <ButtonIcon
          classNames={{ button: s.closeDropdown }}
          icon={'close'}
          onClick={() => setShowDropdown(false)}
          theme={'ghost'}
          size={'db'}
          color={'text'}
        />
      </header>
    );
  };

  const renderCalendar = () => {
    if (modes.calendar === 'week') {
      return (
        <CalendarWeek {...calendar} value={dates} onChange={handleChange} onlyDates={onlyDates} />
      );
    }
    return <Calendar {...calendar} value={dates} onChange={handleChange} onlyDates={onlyDates} />;
  };

  const renderDropdown = () => {
    return (
      <div className={cx(s.dropdown, classNames?.dropdown)}>
        {renderModalHeader()}
        <div className={s.bodyDropdown}>{renderCalendar()}</div>
      </div>
    );
  };

  return (
    <Fragment>
      <Input
        classNames={{
          wrapper: classNames?.wrapper,
          wrapInput: classNames?.wrapInput,
          input: classNames?.input,
        }}
        ref={parent}
        type={'text'}
        size={size}
        icon={icon}
        label={label}
        hint={hint}
        placeholder={placeholder}
        value={renderLabel()}
        error={error}
        errorMsg={errorMsg}
        fakeInput
        required={required}
        loading={loading}
        disabled={disabled}
        onClick={handleDropdown}
        onChange={() => {}}
        test={test}
        iconButton={'close'}
        onClickButton={dates.length && resettable ? handleReset : undefined}
      />
      <Portal
        mounted={showDropdown}
        content={renderDropdown}
        bindTo={parent.current}
        onClose={() => setShowDropdown(false)}
        fullscreen={!minWidth.sm}
        clickToHide
      />
    </Fragment>
  );
};

export default DatePicker;
